import { useState } from 'react';
import { MapPin, Calendar, Users, Star } from 'lucide-react';
import { useNavigate } from "react-router-dom";

import about1 from "../../../assets/about/about1.jpg";
import about2 from "../../../assets/about/about2.jpg";
import about3 from "../../../assets/about/about3.jpg";
import about4 from "../../../assets/about/about4.jpg";

const FeatureCard = ({ image, title, rating }) => (
  <div
    className="bg-white overflow-hidden border-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    style={{ borderWidth: "6px", borderStyle: "solid" }}
  >
    <div className="h-48 overflow-hidden">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
      />
    </div>

    <div className="p-4 border-t border-gray-100">
      <h4 className="text-gray-800 font-semibold text-sm mb-2">
        {title}
      </h4>

      <div className="flex justify-between items-center">
        <div className="flex text-yellow-400">
          {[...Array(rating)].map((_, i) => (
            <Star key={i} size={10} fill="currentColor" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const EventHero = () => {
  const navigate = useNavigate();

  const [eventType, setEventType] = useState("");
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState("");
  const [preference, setPreference] = useState("");
  const [errors, setErrors] = useState({});

  // ✅ VALIDATION FUNCTION (no UI change)
  const validate = () => {
    const newErrors = {};

    if (!eventType.trim()) {
      newErrors.eventType = "Event type is required";
    }

    if (!date.trim()) {
      newErrors.date = "Date is required";
    }

    if (!guests) {
      newErrors.guests = "Guest count is required";
    } else if (isNaN(guests) || Number(guests) <= 0) {
      newErrors.guests = "Enter valid guest count";
    }

    if (!preference.trim()) {
      newErrors.preference = "Preference is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleWhatsApp = () => {
  if (!validate()) return;

  const phoneNumber = "971508536881";

  const message = `Hi, I want to enquire about an event.

 Event Type: ${eventType}
 Date: ${date}
 Guests: ${guests}
 Preference: ${preference}

Please share details.`;

  const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  
  window.open(url, "_blank");

  // ✅ CLEAR FORM AFTER SUBMIT
  setEventType("");
  setDate("");
  setGuests("");
  setPreference("");
  setErrors({});
};

  return (
    <div className="relative w-full bg-white font-sans overflow-x-hidden">

      {/* HERO */}
      <div className="relative min-h-[80vh] w-full flex flex-col items-center justify-center text-center px-4 py-16 md:py-0 md:h-[80vh]">
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: `url(${about1})` }}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative z-10 text-white w-full max-w-5xl mx-auto">
          <p className="italic text-lg md:text-xl font-light mb-2">
            World Best Event Management Agency
          </p>

          <h1 className="text-2xl sm:text-3xl md:text-5xl uppercase tracking-wider mb-6 md:mb-8 px-2">
            Plan Your Upcoming <span className="text-[#39CABB]">Dream Celebration</span>
          </h1>

          {/* SEARCH BAR */}
          <div className="bg-white/95 backdrop-blur-sm p-2 rounded-md shadow-2xl flex flex-col md:flex-row items-stretch md:items-center gap-0 md:gap-2 mx-2 sm:mx-4 md:mx-auto border-b-4 border-[#39CABB]">

            {/* Event Type */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 md:py-2 border-b md:border-b-0 md:border-r border-gray-200">
                <MapPin size={18} className="text-[#39CABB] shrink-0" />
                <input
                  type="text"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  placeholder="Event Type"
                  className="bg-transparent text-gray-700 outline-none w-full text-sm"
                />
              </div>
              {errors.eventType && (
                <span className="text-red-500 text-xs px-4 mt-1">
                  {errors.eventType}
                </span>
              )}
            </div>

            {/* Date */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 md:py-2 border-b md:border-b-0 md:border-r border-gray-200">
                <Calendar size={18} className="text-[#39CABB] shrink-0" />
                <input
                  type="text"
                  value={date}
                  onFocus={(e) => (e.target.type = "date")}
                  onBlur={(e) => {
                    if (!e.target.value) e.target.type = "text";
                  }}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="Date"
                  className="bg-transparent text-gray-700 outline-none w-full text-sm"
                />
              </div>
              {errors.date && (
                <span className="text-red-500 text-xs px-4 mt-1">
                  {errors.date}
                </span>
              )}
            </div>

            {/* Guest Count */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 md:py-2 border-b md:border-b-0 md:border-r border-gray-200">
                <Calendar size={18} className="text-[#39CABB] shrink-0" />
                <input
                  type="text"
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  placeholder="Guest Count"
                  className="bg-transparent text-gray-700 outline-none w-full text-sm"
                />
              </div>
              {errors.guests && (
                <span className="text-red-500 text-xs px-4 mt-1">
                  {errors.guests}
                </span>
              )}
            </div>

            {/* Preference */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 md:py-2 border-b md:border-b-0 flex-1">
                <Users size={18} className="text-[#39CABB] shrink-0" />
                <input
                  type="text"
                  value={preference}
                  onChange={(e) => setPreference(e.target.value)}
                  placeholder="Preference"
                  className="bg-transparent text-gray-700 outline-none w-full text-sm"
                />
              </div>
              {errors.preference && (
                <span className="text-red-500 text-xs px-4 mt-1">
                  {errors.preference}
                </span>
              )}
            </div>

            {/* Button */}
            <button
              onClick={handleWhatsApp}
              className="bg-[#39CABB] hover:bg-[#2da99d] text-white px-8 py-3 rounded uppercase font-bold text-sm transition-all w-full md:w-auto mt-1 md:mt-0"
            >
              Search
            </button>
          </div>

          {/* View Packages */}
          <button
            onClick={() => navigate("/services")}
            className="mt-6 md:mt-8 border-2 border-white/50 bg-white/10 hover:bg-white hover:text-gray-800 transition px-6 py-2 rounded text-xs uppercase tracking-widest font-semibold"
          >
            View Packages
          </button>
        </div>
      </div>

      {/* FEATURE SECTION */}
      <div className="relative z-20 md:-mt-20 max-w-7xl mx-auto px-4 sm:px-6 pb-16 md:pb-20">

        {/* Intro block — full width on mobile, inline on desktop */}
        <div className="pt-10 md:pt-0 pb-6 md:pb-0 md:hidden">
          <h5 className="text-[#39CABB] italic font-medium mb-1">Our Agency</h5>
          <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-[#39CABB] pl-4 mb-2">
            Feature Event
          </h2>
          <p className="text-gray-400 text-xs leading-relaxed mb-4">
    Delivering exceptional events with innovative planning, creative design, and seamless execution that ensures unforgettable experiences for every client and guest.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 md:gap-6 items-center">

          {/* Desktop-only text column */}
          <div className="hidden md:block pt-24 space-y-4">
            <h5 className="text-[#39CABB] italic font-medium">Our Agency</h5>
            <h2 className="text-3xl font-bold text-gray-800 border-l-4 border-[#39CABB] pl-4">
              Feature Event
            </h2>
            <p className="text-gray-400 text-xs leading-relaxed">
    Delivering exceptional events with innovative planning, creative design, and seamless execution that ensures unforgettable experiences for every client and guest.
            </p>
          </div>

          <FeatureCard
            image={about2}
            title="Luxury Wedding Planning"
            rating={4}
          />
          <FeatureCard
            image={about3}
            title="Corporate Events"
            rating={5}
          />
          <FeatureCard
            image={about4}
            title="AV Rentals"
            rating={5}
          />
        </div>
      </div>    </div>
  );
};

export default EventHero;